import { useState, useMemo } from 'react';
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useAllVerificationApplications } from '../../hooks/useVerification';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { formatRelativeTime } from '../../utils/formatters';

const FILTERS = ['all', 'pending', 'approved', 'rejected'];

export default function AdminVerifications() {
  const { applications, loading } = useAllVerificationApplications();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const filtered = useMemo(() => {
    let list = applications;
    if (filter !== 'all') {
      list = list.filter((a) => a.status === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.realFullName?.toLowerCase().includes(q) ||
          a.userAnonymizedUsername?.toLowerCase().includes(q) ||
          a.contactEmail?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [applications, search, filter]);

  const pendingCount = applications.filter((a) => a.status === 'pending').length;

  if (loading) return <LoadingSpinner message="Loading applications..." />;

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between" align="center">
        <Box>
          <Heading size="lg">Verifications</Heading>
          <Text color="gray.500" mt={1}>
            {applications.length} total applications
            {pendingCount > 0 && ` · ${pendingCount} pending`}
          </Text>
        </Box>
      </HStack>

      <HStack spacing={3} flexWrap="wrap">
        {FILTERS.map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? 'solid' : 'outline'}
            colorScheme={filter === f ? 'orange' : 'gray'}
            onClick={() => setFilter(f)}
            textTransform="capitalize"
          >
            {f}
          </Button>
        ))}
      </HStack>

      <InputGroup maxW="400px">
        <InputLeftElement pointerEvents="none">
          <Icon as={FiSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Search by name, username, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </InputGroup>

      <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
        <Box overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Username</Th>
                <Th>Contact Email</Th>
                <Th>Status</Th>
                <Th>Submitted</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((app) => (
                <Tr key={app.id}>
                  <Td>
                    <Text
                      as={Link}
                      to={`/admin/verifications/${app.id}`}
                      fontWeight="medium"
                      color="orange.500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      {app.realFullName}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600">{app.userAnonymizedUsername}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600">{app.contactEmail}</Text>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={
                        app.status === 'approved'
                          ? 'green'
                          : app.status === 'rejected'
                          ? 'red'
                          : 'orange'
                      }
                      fontSize="xs"
                    >
                      {app.status}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="xs" color="gray.500">
                      {formatRelativeTime(app.createdAt)}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
        {filtered.length === 0 && (
          <Box py={8} textAlign="center">
            <Text color="gray.500">No applications found</Text>
          </Box>
        )}
      </Box>
    </VStack>
  );
}
